// src/Components/admin/AdminSubscriptionModal.jsx
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { vpsData, LINUX_DISTROS } from '../../assets/data/vpsData';
import { customLimits } from '../../assets/data/customLimits';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://192.30.192.47:7501/api";

const WINDOWS_VERSIONS = ["Windows Server 2019", "Windows Server 2022"];
const CPU_PRESETS = [2, 4, 8, 16, 32];
const RAM_PRESETS = [2, 4, 8, 16, 32, 64];
const NVME_PRESETS = [30, 50, 100, 200, 500];
const SSD_PRESETS = [0, 100, 500, 1000, 2000];
// Plan dropdown value for storage sizes not in vpsData (admin-only, never shown on public pricing)
const CUSTOM_STORAGE = '__custom_storage__';

const SUB_TYPES = [
  { key: 'vps', label: 'VPS Hosting' },
  { key: 'storage', label: 'Cloud Storage' },
  { key: 'custom', label: 'Custom Build' },
  { key: 'firewall', label: 'Firewall' },
  { key: 'vapt', label: 'VAPT' },
];

const inputClass = "w-full bg-[#0A2A4A] border border-[#1A3A5A] rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500";

const billingMonthsFor = (cycle) => cycle === 'Quarterly' ? 3 : cycle === 'Annually' ? 12 : 1;

// Strict DD/MM/YYYY parse → Date at local midnight, or null if invalid
const parseEnGbDate = (s) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec((s || '').trim());
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (month < 1 || month > 12 || year < 1900 || year > 2100) return null;
  const d = new Date(year, month - 1, day);
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null;
  return d;
};

const formatEnGb = (d) =>
  `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;

// Mirror Java LocalDate.plusMonths (clamp month-end: Jan 31 + 1mo → Feb 28)
const addMonthsClamped = (date, months) => {
  const day = date.getDate();
  const target = new Date(date.getFullYear(), date.getMonth() + months, 1);
  const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(day, lastDay));
  return target;
};

const AdminSubscriptionModal = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customerName: '',
    email: '',
    phone: '',
    selectedPlan: '',
    selectedOS: 'Linux',
    selectedVariant: '',
    quantity: 1,
    billingCycle: 'Monthly',
    startDate: '',
    expirationDate: '',
    dedicatedIPs: 0,
    extraStorage: { nvme: 0, hdd: 0 }
  });

  // Custom-spec state (specs only — no pricing on admin side)
  const [cpuCores, setCpuCores] = useState(customLimits.cpu.default);
  const [ramGb, setRamGb] = useState(customLimits.ramGb.default);
  const [customNvme, setCustomNvme] = useState(customLimits.nvmeGb.default);
  const [customSsd, setCustomSsd] = useState(customLimits.hddGb.default);
  const [customStorageTb, setCustomStorageTb] = useState('');
  // Firewall type / VAPT details (free text, stored as the plan)
  const [securityDetails, setSecurityDetails] = useState('');
  const [notes, setNotes] = useState('');

  const [variants, setVariants] = useState([]);
  const [isStorageOnly, setIsStorageOnly] = useState(false);
  const [subType, setSubType] = useState('vps');
  // True once the admin hand-types expiration — auto-calc stops (manual wins)
  const [expirationEdited, setExpirationEdited] = useState(false);

  const isCustom = subType === 'custom';
  // Firewall/VAPT: customer + details + renewal date only (no plan, OS, specs or billing)
  const isSecurity = subType === 'firewall' || subType === 'vapt';
  const detailsLabel = subType === 'firewall' ? 'Firewall Type' : 'VAPT Details';
  const isCustomStorage = formData.selectedPlan === CUSTOM_STORAGE;
  const filteredPlans = vpsData.filter(p => subType === 'storage' ? p.isStorageOnly : !p.isStorageOnly);

  const handleSubTypeChange = (type) => {
    setSubType(type);
    setVariants([]);
    setIsStorageOnly(false);
    setCustomStorageTb('');
    setSecurityDetails('');
    if (type === 'firewall' || type === 'vapt') {
      // Start is the entry date; renewal defaults to a year out (auto-calc), editable
      setExpirationEdited(false);
      setFormData(prev => ({
        ...prev,
        selectedPlan: '',
        selectedOS: 'N/A',
        selectedVariant: '',
        billingCycle: 'Annually',
        startDate: formatEnGb(new Date())
      }));
    } else if (type === 'custom') {
      setFormData(prev => ({
        ...prev,
        selectedPlan: 'Custom',
        selectedOS: 'Linux',
        selectedVariant: LINUX_DISTROS[0]
      }));
      setVariants(LINUX_DISTROS);
    } else {
      setFormData(prev => ({
        ...prev,
        selectedPlan: '',
        selectedOS: 'Linux',
        selectedVariant: ''
      }));
    }
  };

  // Set default dates (start = today, expiration = start + 1 month for default Monthly cycle)
  useEffect(() => {
    const today = new Date();
    const startDate = formatEnGb(today);
    const expirationDate = formatEnGb(addMonthsClamped(today, billingMonthsFor('Monthly')));

    setFormData(prev => ({
      ...prev,
      startDate,
      expirationDate
    }));
  }, []);

  // Auto-calculate expiration from start date + billing cycle (unless admin overrode it)
  useEffect(() => {
    if (expirationEdited) return;
    const start = parseEnGbDate(formData.startDate);
    if (!start) return;
    const exp = formatEnGb(addMonthsClamped(start, billingMonthsFor(formData.billingCycle)));
    setFormData(prev => (prev.expirationDate === exp ? prev : ({ ...prev, expirationDate: exp })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.startDate, formData.billingCycle, expirationEdited]);

  const resetExpirationToAuto = () => {
    setExpirationEdited(false);
    const start = parseEnGbDate(formData.startDate);
    if (start) {
      const exp = formatEnGb(addMonthsClamped(start, billingMonthsFor(formData.billingCycle)));
      setFormData(prev => ({ ...prev, expirationDate: exp }));
    }
  };

  // Custom mode: keep variant list in sync with OS
  useEffect(() => {
    if (!isCustom) return;
    const list = formData.selectedOS === 'Linux' ? LINUX_DISTROS : WINDOWS_VERSIONS;
    setVariants(list);
    if (!list.includes(formData.selectedVariant)) {
      setFormData(prev => ({ ...prev, selectedVariant: list[0] }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.selectedOS, isCustom]);

  // Update variants when OS or plan changes (bundled plans only)
  useEffect(() => {
    if (isCustom || isSecurity) return;
    if (isCustomStorage) {
      // Custom storage size - same as a storage plan, but not in vpsData
      setIsStorageOnly(true);
      setVariants([]);
      setFormData(prev => ({
        ...prev,
        selectedOS: 'Storage Only',
        selectedVariant: ''
      }));
      return;
    }
    const planData = vpsData.find(p => p.plan === formData.selectedPlan);
    if (planData) {
      const isStorage = planData.isStorageOnly || false;
      setIsStorageOnly(isStorage);

      if (isStorage) {
        // Storage plan - no OS or variants
        setVariants([]);
        setFormData(prev => ({
          ...prev,
          selectedOS: 'Storage Only',
          selectedVariant: ''
        }));
        return;
      }

      const osKey = formData.selectedOS.toLowerCase();
      const osOptions = planData.osOptions?.[osKey];
      if (osOptions) {
        const variantList = formData.selectedOS === 'Linux' ? osOptions.distros || [] : osOptions.versions || [];
        setVariants(variantList);
        if (variantList.length > 0 && (!formData.selectedVariant || !variantList.includes(formData.selectedVariant))) {
          setFormData(prev => ({ ...prev, selectedVariant: variantList[0] }));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.selectedPlan, formData.selectedOS, isCustom]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Hand-typed expiration wins over auto-calc from then on
  const handleExpirationChange = (e) => {
    setExpirationEdited(true);
    setFormData(prev => ({ ...prev, expirationDate: e.target.value }));
  };

  const handleStorageChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      extraStorage: { ...prev.extraStorage, [type]: parseInt(value) || 0 }
    }));
  };

  const clamp = (v, min, max) => Math.min(max, Math.max(min, Number(v) || 0));

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate
    if (!formData.customerName || !formData.email) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (isSecurity && !securityDetails.trim()) {
      toast.error(`Please enter the ${detailsLabel.toLowerCase()}`);
      return;
    }

    if (!isCustom && !isSecurity && !formData.selectedPlan) {
      toast.error('Please select a plan');
      return;
    }

    if (isCustomStorage && !/^[1-9]\d*$/.test(customStorageTb.trim())) {
      toast.error('Storage size must be a whole number of at least 1 TB');
      return;
    }

    if (isCustom) {
      if (cpuCores < 1) {
        toast.error('CPU cores must be at least 1');
        return;
      }
      if (ramGb < 2) {
        toast.error('RAM must be at least 2 GB');
        return;
      }
    }

    if (!isSecurity && !isStorageOnly && !formData.selectedVariant) {
      toast.error('Please select a variant');
      return;
    }

    const expLabel = isSecurity ? 'Renewal date' : 'Expiration date';

    const start = parseEnGbDate(formData.startDate);
    if (!start) {
      toast.error('Start date must be a valid date in DD/MM/YYYY format');
      return;
    }

    const exp = parseEnGbDate(formData.expirationDate);
    if (!exp) {
      toast.error(`${expLabel} must be a valid date in DD/MM/YYYY format`);
      return;
    }

    if (exp <= start) {
      toast.error(isSecurity ? 'Renewal date must be after today' : 'Expiration date must be after the start date');
      return;
    }

    setLoading(true);

    try {
      const subscriptionData = isSecurity
        ? {
            // Firewall/VAPT: details + renewal date only (backend stores zeros, OS N/A)
            Name: formData.customerName,
            Email: formData.email,
            Phone: formData.phone || undefined,
            ServiceType: subType === 'firewall' ? 'FIREWALL' : 'VAPT',
            Plan: securityDetails.trim(),
            OS: 'N/A',
            Quantity: 1,
            BillingCycle: 'Annually',
            Start: formData.startDate,
            Expiration: formData.expirationDate,
            Notes: notes.trim() || undefined
          }
        : isCustom
        ? {
            // Custom specs: customer + specs only, no pricing (backend stores zeros)
            Name: formData.customerName,
            Email: formData.email,
            Phone: formData.phone || undefined,
            ServiceType: 'VPS',
            Plan: 'Custom',
            OS: `${formData.selectedOS} (${formData.selectedVariant})`,
            Quantity: formData.quantity,
            BillingCycle: formData.billingCycle,
            Start: formData.startDate,
            Expiration: formData.expirationDate,
            DedicatedIPs: formData.dedicatedIPs || 0,
            ExtraStorage: {
              nvme: customNvme || 0,
              hdd: customSsd || 0
            },
            CpuCores: cpuCores,
            RamGb: ramGb,
            IsCustom: true,
            Notes: notes.trim() || undefined
          }
        : {
            // Bundled plans: no pricing on admin side either (backend stores zeros)
            Name: formData.customerName,
            Email: formData.email,
            Phone: formData.phone || undefined,
            ServiceType: subType === 'storage' ? 'CLOUD' : 'VPS',
            Plan: isCustomStorage ? `Cloud Storage - ${customStorageTb.trim()}TB` : formData.selectedPlan,
            OS: isStorageOnly ? 'Storage Only' : `${formData.selectedOS} (${formData.selectedVariant})`,
            Quantity: formData.quantity,
            BillingCycle: formData.billingCycle,
            Start: formData.startDate,
            Expiration: formData.expirationDate,
            DedicatedIPs: isStorageOnly ? 0 : (formData.dedicatedIPs || 0),
            ExtraStorage: isStorageOnly ? { nvme: 0, hdd: 0 } : {
              nvme: formData.extraStorage.nvme || 0,
              hdd: formData.extraStorage.hdd || 0
            },
            Notes: notes.trim() || undefined
          };

      const token = localStorage.getItem('authToken');
      const response = await axios.post(
        `${API_BASE_URL}/admin/subscriptions`,
        subscriptionData,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      console.log('Subscription created:', response.data);
      toast.success('Subscription created successfully!');

      if (onSuccess) onSuccess();
      if (onClose) onClose();
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error(error.response?.data?.message || 'Failed to create subscription');
    } finally {
      setLoading(false);
    }
  };

  const title = subType === 'storage' ? 'Cloud Storage'
    : subType === 'custom' ? 'Custom Specs'
    : subType === 'firewall' ? 'Firewall'
    : subType === 'vapt' ? 'VAPT'
    : 'Subscription';

  return (
    <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50 backdrop-blur-md" onClick={onClose}>
      <div className="bg-[#001F3F] border-2 border-[#022A52] rounded-3xl p-8 w-full max-w-2xl relative text-white max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-300 hover:text-red-500 text-2xl font-bold"
        >
          ×
        </button>

        <h2 className="text-2xl font-bold mb-6 text-center">
          Add {title} (Admin)
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Subscription Type */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-1">Subscription Type *</label>
              <div className="bg-[#0A2A4A] border border-[#1A3A5A] rounded-lg p-1 flex flex-wrap">
                {SUB_TYPES.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleSubTypeChange(key)}
                    className={`flex-1 basis-1/3 sm:basis-0 py-2 px-2 rounded-md text-sm font-medium whitespace-nowrap transition-all duration-300 ${
                      subType === key
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Customer Name */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-1">Customer Name *</label>
              <input
                type="text"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
                className={inputClass}
                placeholder="John Doe"
                required
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Email *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={inputClass}
                placeholder="john@example.com"
                required
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">Phone</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className={inputClass}
                placeholder="+233 XX XXX XXXX"
              />
            </div>

            {/* Firewall type / VAPT details */}
            {isSecurity && (
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1">{detailsLabel} *</label>
                <input
                  type="text"
                  value={securityDetails}
                  onChange={(e) => setSecurityDetails(e.target.value)}
                  maxLength={255}
                  className={inputClass}
                  placeholder={subType === 'firewall' ? 'e.g. FortiGate 60F' : 'e.g. External network + web app, 3 IPs'}
                  required
                />
              </div>
            )}

            {/* Plan (bundled only — custom is always "Custom") */}
            {!isCustom && !isSecurity && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Plan *</label>
                <select
                  name="selectedPlan"
                  value={formData.selectedPlan}
                  onChange={handleChange}
                  className={inputClass}
                  required
                >
                  <option value="">Select Plan</option>
                  {filteredPlans.map(plan => (
                    <option key={plan.plan} value={plan.plan}>
                      {plan.plan}
                    </option>
                  ))}
                  {subType === 'storage' && (
                    <option value={CUSTOM_STORAGE}>Custom size…</option>
                  )}
                </select>
              </div>
            )}

            {/* Custom storage size */}
            {isCustomStorage && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Storage size (TB) *</label>
                <input
                  type="number"
                  value={customStorageTb}
                  onChange={(e) => setCustomStorageTb(e.target.value)}
                  min="1"
                  step="1"
                  className={inputClass}
                  placeholder="e.g. 4"
                  required
                />
              </div>
            )}

            {isCustom && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Plan</label>
                <div className={inputClass + " bg-[#0A2A4A] text-gray-200"}>
                  Custom
                </div>
              </div>
            )}

            {/* OS - Hide for storage plans */}
            {!isStorageOnly && !isSecurity && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">OS *</label>
                <select
                  name="selectedOS"
                  value={formData.selectedOS}
                  onChange={handleChange}
                  className={inputClass}
                  required
                >
                  <option value="Linux">Linux</option>
                  <option value="Windows">Windows</option>
                </select>
              </div>
            )}

            {/* Variant - Hide for storage plans */}
            {!isStorageOnly && !isSecurity && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Variant *</label>
                <select
                  name="selectedVariant"
                  value={formData.selectedVariant}
                  onChange={handleChange}
                  className={inputClass}
                  required
                  disabled={variants.length === 0}
                >
                  <option value="">Select Variant</option>
                  {variants.map(v => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
                {variants.length === 0 && formData.selectedPlan && !isCustom && (
                  <p className="text-xs text-yellow-400 mt-1">No variants available for this OS</p>
                )}
              </div>
            )}

            {/* Custom specs (no pricing) */}
            {isCustom && (
              <div className="col-span-2 bg-[#0A2A4A] border border-[#1A3A5A] rounded-lg p-4 space-y-4">
                <p className="text-sm text-blue-300">
                  <strong>⚙️ Custom specs</strong> — no payment on the admin portal, pricing stored as zero.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">vCPU Cores * (min {customLimits.cpu.min})</label>
                    <input
                      type="number"
                      value={cpuCores}
                      min={customLimits.cpu.min}
                      max={customLimits.cpu.max}
                      step={customLimits.cpu.step}
                      onChange={(e) => setCpuCores(clamp(e.target.value, customLimits.cpu.min, customLimits.cpu.max))}
                      className={inputClass}
                    />
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {CPU_PRESETS.map(p => (
                        <button key={p} type="button" onClick={() => setCpuCores(p)}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition ${cpuCores === p ? 'bg-blue-600 text-white' : 'bg-[#022A52] text-gray-300 hover:bg-[#033A6A] border border-[#033A6A]'}`}>
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">RAM (GB) * (min {customLimits.ramGb.min})</label>
                    <input
                      type="number"
                      value={ramGb}
                      min={customLimits.ramGb.min}
                      max={customLimits.ramGb.max}
                      step={customLimits.ramGb.step}
                      onChange={(e) => setRamGb(clamp(Math.round((Number(e.target.value) || 0) / 2) * 2, customLimits.ramGb.min, customLimits.ramGb.max))}
                      className={inputClass}
                    />
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {RAM_PRESETS.map(p => (
                        <button key={p} type="button" onClick={() => setRamGb(p)}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition ${ramGb === p ? 'bg-blue-600 text-white' : 'bg-[#022A52] text-gray-300 hover:bg-[#033A6A] border border-[#033A6A]'}`}>
                          {p} GB
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">NVMe Storage (GB)</label>
                    <input
                      type="number"
                      value={customNvme}
                      min={customLimits.nvmeGb.min}
                      max={customLimits.nvmeGb.max}
                      step="1"
                      onChange={(e) => setCustomNvme(clamp(e.target.value, customLimits.nvmeGb.min, customLimits.nvmeGb.max))}
                      className={inputClass}
                    />
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {NVME_PRESETS.map(p => (
                        <button key={p} type="button" onClick={() => setCustomNvme(p)}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition ${customNvme === p ? 'bg-blue-600 text-white' : 'bg-[#022A52] text-gray-300 hover:bg-[#033A6A] border border-[#033A6A]'}`}>
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-1">SSD Storage (GB)</label>
                    <input
                      type="number"
                      value={customSsd}
                      min={customLimits.hddGb.min}
                      max={customLimits.hddGb.max}
                      step="1"
                      onChange={(e) => setCustomSsd(clamp(e.target.value, customLimits.hddGb.min, customLimits.hddGb.max))}
                      className={inputClass}
                    />
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {SSD_PRESETS.map(p => (
                        <button key={p} type="button" onClick={() => setCustomSsd(p)}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition ${customSsd === p ? 'bg-blue-600 text-white' : 'bg-[#022A52] text-gray-300 hover:bg-[#033A6A] border border-[#033A6A]'}`}>
                          {p}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Storage Plan Info */}
            {isStorageOnly && (
              <div className="col-span-2 bg-blue-900/30 p-4 rounded-lg border border-blue-500/30">
                <p className="text-blue-300 text-sm">
                  <strong>📦 Cloud Storage Plan</strong><br />
                  {vpsData.find(p => p.plan === formData.selectedPlan)?.osOptions?.storage?.description || "Pure cloud storage without compute resources"}
                </p>
              </div>
            )}

            {!isSecurity && (
              <>
                {/* Quantity */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Quantity</label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleChange}
                    min="1"
                    className={inputClass}
                  />
                </div>

                {/* Billing Cycle */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Billing Cycle</label>
                  <select
                    name="billingCycle"
                    value={formData.billingCycle}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Annually">Annually</option>
                  </select>
                </div>
              </>
            )}

            {/* Dedicated IPs - Hide for storage plans, show for custom (spec only, no charge) */}
            {!isStorageOnly && !isSecurity && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Dedicated IPs (spec only — no charge)
                </label>
                <input
                  type="number"
                  name="dedicatedIPs"
                  value={formData.dedicatedIPs}
                  onChange={handleChange}
                  min="0"
                  className={inputClass}
                />
              </div>
            )}

            {/* Start Date (Firewall/VAPT: entry date, not shown) */}
            {!isSecurity && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Start Date *</label>
                <input
                  type="text"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="DD/MM/YYYY"
                  required
                />
              </div>
            )}

            {/* Expiration Date (Renewal Date for Firewall/VAPT) */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {isSecurity ? 'Renewal Date' : 'Expiration Date'} *{' '}
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${expirationEdited ? 'bg-yellow-600/30 text-yellow-300' : 'bg-green-600/30 text-green-300'}`}>
                  {expirationEdited ? 'Manual' : 'Auto'}
                </span>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="expirationDate"
                  value={formData.expirationDate}
                  onChange={handleExpirationChange}
                  className={inputClass}
                  placeholder="DD/MM/YYYY"
                  required
                />
                {expirationEdited && (
                  <button
                    type="button"
                    onClick={resetExpirationToAuto}
                    title="Recompute from start date + billing cycle"
                    className="shrink-0 px-3 py-2 bg-[#0A2A4A] border border-[#1A3A5A] rounded-lg text-blue-300 hover:text-white hover:border-blue-500 transition"
                  >
                    ↺
                  </button>
                )}
              </div>
              <p className="text-[11px] text-gray-500 mt-1">
                {isSecurity
                  ? 'Defaults to one year from today. Enter the next renewal date.'
                  : 'Auto-calculated: Monthly +1 mo, Quarterly +3, Annually +12. Editable.'}
              </p>
            </div>

            {/* Extra Storage - Hide for storage plans and custom (custom uses spec inputs above) */}
            {!isStorageOnly && !isCustom && !isSecurity && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Extra Storage NVMe (GB)</label>
                  <input
                    type="number"
                    value={formData.extraStorage.nvme}
                    onChange={(e) => handleStorageChange('nvme', e.target.value)}
                    min="0"
                    step="1"
                    className={inputClass}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Extra Storage SSD (GB)</label>
                  <input
                    type="number"
                    value={formData.extraStorage.hdd}
                    onChange={(e) => handleStorageChange('hdd', e.target.value)}
                    min="0"
                    step="1"
                    className={inputClass}
                    placeholder="0"
                  />
                </div>
              </>
            )}

            {/* Add-ons & Notes */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-300 mb-1">
                {isSecurity
                  ? 'Notes'
                  : subType === 'storage'
                    ? 'Extra Notes (included services are added automatically)'
                    : 'Extra Notes (add-ons are included automatically)'}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                maxLength={2000}
                className={inputClass}
                placeholder={isSecurity
                  ? 'Optional — included in the customer\'s confirmation email'
                  : 'e.g. Windows BYOL, special requests — no need to type add-ons'}
              />
            </div>
          </div>

          <div className="flex justify-end space-x-4 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-600 hover:bg-gray-700 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Subscription'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminSubscriptionModal;
